/**
 * BomRowExportPopover — row-level single-SKU BOM download (Feb 2026).
 *
 * Replaces the standalone `<ExportBomModal>` as the single-SKU export
 * entry point. The row already supplies `buyerSkuId`, so no search step
 * is needed — a small popover with XLSX and PDF choices does the whole
 * job in situ.
 *
 * Download logic mirrors ExportBomModal::handleDownload exactly (axios
 * blob GET → file-saver). We deliberately do NOT reuse that Dialog by
 * conditionally hiding parts of it — a purpose-built Popover is a
 * simpler surface for a two-choice prompt anchored to a table row.
 */
import { useState, useCallback } from "react";
import axios from "axios";
import { saveAs } from "file-saver";
import { toast } from "sonner";
import { Download, FileSpreadsheet, FileText, Loader2 } from "lucide-react";

import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import {
  Tooltip, TooltipContent, TooltipTrigger,
} from "@/components/ui/tooltip";
import useAuthStore from "@/store/authStore";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const BomRowExportPopover = ({ buyerSkuId, disabled = false }) => {
  const token = useAuthStore((s) => s.token);
  const [open, setOpen] = useState(false);
  const [downloading, setDownloading] = useState(null); // 'xlsx' | 'pdf' | null

  const handleDownload = useCallback(async (fmt, e) => {
    // Stop the row's expand-toggle from firing when the user picks an option.
    e?.stopPropagation?.();
    if (!buyerSkuId || downloading) return;
    setDownloading(fmt);
    try {
      const r = await axios.get(
        `${API}/bom/${buyerSkuId}/export?format=${fmt}`,
        {
          headers: { Authorization: `Bearer ${token}` },
          responseType: "blob",
        },
      );
      const mime = fmt === "pdf"
        ? "application/pdf"
        : "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet";
      const stamp = new Date().toISOString().slice(0, 10);
      saveAs(new Blob([r.data], { type: mime }), `bom_${buyerSkuId}_${stamp}.${fmt}`);
      toast.success(`${fmt.toUpperCase()} download started`);
      setOpen(false);
    } catch (err) {
      const detail = err?.response?.data?.detail || `Failed to export ${fmt.toUpperCase()}`;
      toast.error(typeof detail === "string" ? detail : `Failed to export ${fmt.toUpperCase()}`);
    } finally {
      setDownloading(null);
    }
  }, [buyerSkuId, token, downloading]);

  // Disabled variant — icon stays visible so the affordance is discoverable
  // (AC2 explicitly wants "disabled, not hidden"), wrapped in a tooltip
  // that explains why. No Popover attached — nothing to open.
  if (disabled) {
    return (
      <Tooltip>
        <TooltipTrigger asChild>
          <button
            type="button"
            disabled
            aria-disabled="true"
            onClick={(e) => e.stopPropagation()}
            className="inline-flex items-center justify-center h-7 w-7 rounded text-zinc-300 cursor-not-allowed"
            data-testid={`bom-row-export-${buyerSkuId}`}
          >
            <Download className="w-4 h-4" />
          </button>
        </TooltipTrigger>
        <TooltipContent side="left" className="font-mono text-xs">
          No BOM to export
        </TooltipContent>
      </Tooltip>
    );
  }

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <button
          type="button"
          onClick={(e) => e.stopPropagation()}
          className="inline-flex items-center justify-center h-7 w-7 rounded text-zinc-600 hover:bg-zinc-100 hover:text-zinc-900 transition-colors"
          data-testid={`bom-row-export-${buyerSkuId}`}
          title="Download this SKU's BOM"
        >
          <Download className="w-4 h-4" />
        </button>
      </PopoverTrigger>
      <PopoverContent
        side="left"
        align="start"
        className="w-44 p-1"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="px-2 py-1.5 text-[10px] font-mono uppercase text-muted-foreground">
          Download format
        </div>
        <button
          type="button"
          onClick={(e) => handleDownload("xlsx", e)}
          disabled={!!downloading}
          className="w-full flex items-center gap-2 px-2 py-1.5 rounded text-sm hover:bg-zinc-100 disabled:opacity-60 disabled:cursor-wait"
          data-testid={`bom-row-export-xlsx-${buyerSkuId}`}
        >
          {downloading === "xlsx"
            ? <Loader2 className="w-4 h-4 animate-spin" />
            : <FileSpreadsheet className="w-4 h-4 text-emerald-700" />}
          <span>XLSX</span>
        </button>
        <button
          type="button"
          onClick={(e) => handleDownload("pdf", e)}
          disabled={!!downloading}
          className="w-full flex items-center gap-2 px-2 py-1.5 rounded text-sm hover:bg-zinc-100 disabled:opacity-60 disabled:cursor-wait"
          data-testid={`bom-row-export-pdf-${buyerSkuId}`}
        >
          {downloading === "pdf"
            ? <Loader2 className="w-4 h-4 animate-spin" />
            : <FileText className="w-4 h-4 text-rose-700" />}
          <span>PDF</span>
        </button>
      </PopoverContent>
    </Popover>
  );
};

export default BomRowExportPopover;
